import { useEffect, useRef, useState, useCallback } from "react"
import api from "../../../api/api"
import { useAppDispatch } from "../../../redux/store"
import { setSilentEvent } from "../../../redux/slices/events/eventsSlice"

const POLL_INTERVAL = 15_000

interface BackgroundRefreshParams {
  eventId: string | undefined
}

export function useBackgroundRefresh({ eventId }: BackgroundRefreshParams) {
  const dispatch = useAppDispatch()
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const silentFetch = useCallback(async () => {
    if (document.visibilityState !== "visible" || !eventId) return

    try {
      const eventRes = await api.events.getEvent(eventId)
      dispatch(setSilentEvent(eventRes.data.data))
      setLastRefreshed(new Date())
    } catch {
      // Silent failure — don't disrupt the user
    }
  }, [dispatch, eventId])

  useEffect(() => {
    if (!eventId) return

    setLastRefreshed(new Date())

    intervalRef.current = setInterval(silentFetch, POLL_INTERVAL)

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        silentFetch()
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [eventId, silentFetch])

  return { lastRefreshed }
}
